import HorizontalChipsWithTooltip from '@common/components/custom/CustomInputs/HorizontalChipsWithTooltip';
import CarContainer1, { NormalCard, VehicleListingCard, CarListingCard } from '@common/pages/Dashboard/components/CarContainer1';
import CarsList from '@common/pages/Dashboard/components/CarsList';
import { auctionTypes } from '@common/utils/constants/auction';
import { carContainerTypes } from '@common/utils/constants/ui/carContainers';
import { years } from '@common/utils/constants/years';
import { CancelTwoTone, Check } from '@mui/icons-material';
import {
  Box,
  Button,
  InputLabel,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import debounce from 'lodash.debounce'; // Import debounce function
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { baseImageUrl } from '../../../../api.js';
import theme from '../../../../theme.js';
import {
  useFetchOwnCarsQuery,
  useGetVehicleModelsMutation,
  useLazyFetchOtherCarsQuery,
  useLazyFetchOwnCarsQuery,
  useLazyGetVehicleMakesQuery
} from '../../../../toolkit/upcomingCars/upcomingCarsApi';
import MyCarsOrOtherCarsButton, { defaultValue } from '../common/MyCarsOrOtherCarsButton.jsx';
import { useCancelAuctionHandler } from '../LiveAuction/customHooks/useCancelAuctionHandler.js';
import { convertFromUTCToGivenTimezone, convertUtcStringFormat } from '@common/utils/methods/dateTime.js';
import { reducerPaths } from '@common/app/reducerPaths.js';
import { getUpcomingCarRemainingSeconds } from '@common/utils/methods/generalVehicleMethods.js';

const UpcomingCarsList = ({ setDetailView, setMyCarsOrOtherCars, onCarClick }) => {
  // useDispatch...
  const dispatch = useDispatch();
  const { t } = useTranslation('upcomingCars');
  const muiTheme = useTheme();

  // Responsive breakpoints
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));
  // useDispatch...

  const auth = useSelector(store => store[reducerPaths.auth]);

  // Helper function to get selected makes render value - Fixed with translations
  const getSelectedMakesRenderValue = (selected) => {
    if (!selected || selected.filter((item) => item && item.value).length === 0) {
      return t('select');
    }
    const filteredSelected = selected.filter((item) => item && item.value);
    if (filteredSelected.length === 1) {
      return filteredSelected[0]?.label || t('select');
    }
    return `${filteredSelected.length} ${t('selectMake')}`;
  };

  // Helper function to get selected models render value - Fixed with translations
  const getSelectedModelsRenderValue = (selected) => {
    if (!selected || selected.filter((item) => item && item.value).length === 0) {
      return t('select');
    }
    const filteredSelected = selected.filter((item) => item && item.value);
    if (filteredSelected.length === 1) {
      return filteredSelected[0]?.label || t('select');
    }
    return `${filteredSelected.length} ${t('selectModel')}`;
  };

  const pageTabs = React.useMemo(() => {
    return {
      liveAuction: 'live_auction',
      buyNow: 'buy_now',
    };
  }, []);

  const pageTabsReverseMapping = React.useMemo(() => {
    return {
      live_auction: 'liveAuction',
      buy_now: 'buyNow',
    };
  }, []);

  const paginationDataInit = React.useMemo(() => {
    return {
      current_page: 1,
      per_page: 10,
      isFetching: true,
      data: [],
    };
  }, []);

  const [makeOptions, setMakeOptions] = React.useState([]);
  const [yearOptions, setYearOptions] = React.useState([]);
  const [modelOptions, setModelOptions] = React.useState([]);
  const [isModelOptionsLoading, setIsModelOptionsLoading] = React.useState(false);

  // paginated data...
  const [carList, setCarList] = React.useState([]);
  // paginated data...

  const [allMakes, setAllMakes] = React.useState([]);
  const [allModels, setAllModels] = React.useState([]);
  const [allYears, setAllYears] = React.useState([]);

  // Fixed: Changed from [""] to [] for proper empty state handling
  const [selectedMakes, setSelectedMakes] = React.useState([]);
  const [selectedModels, setSelectedModels] = React.useState([]);
  const [selectedYearFrom, setSelectedYearFrom] = React.useState('');
  const [selectedYearTo, setSelectedYearTo] = React.useState('');
  const [odometerFrom, setOdometerFrom] = React.useState('');
  const [odometerTo, setOdometerTo] = React.useState('');
  const [selectedBrands, setSelectedBrands] = React.useState([]);

  const [paginationData, setPaginationData] = React.useState(paginationDataInit);
  const [value, setValue] = React.useState(defaultValue);

  const [tabValue, setTabValue] = React.useState(0);
  const [pageTab, setPageTab] = React.useState(pageTabs?.liveAuction);

  const params = React.useMemo(() => {
    return {
      Year: selectedYearFrom,
      Make: selectedMakes
        ?.filter((item) => item && item.value)
        ?.map((item) => item?.label)
        ?.join(','), // Pass make names
      Model: selectedModels
        ?.filter((item) => item && item.value)
        ?.map((item) => item?.label)
        ?.join(','), // Pass model names
      from_year: selectedYearFrom,
      to_year: selectedYearTo,
      from_Mileage: odometerFrom,
      to_Mileage: odometerTo,
      auction_type: pageTab,
      page: paginationData?.current_page,
      per_page: paginationData?.per_page,
    };
  }, [selectedMakes, selectedModels, selectedYearFrom, selectedYearTo, odometerFrom, odometerTo, pageTab]);

  // custom hooks start...
  const fetchCarsResponse = useFetchOwnCarsQuery({
    page: paginationData.current_page,
    per_page: paginationData.per_page,
  });
  const [getFetchOwnCarsApiHandler, getFetchOwnCarsApiData] = useLazyFetchOwnCarsQuery();
  const [getFetchOtherCarsApiHandler, getFetchOtherCarsApiData] = useLazyFetchOtherCarsQuery();
  const [getLiveAuctionVehicleMakesApiHandler, getLiveAuctionVehicleMakesApiData] = useLazyGetVehicleMakesQuery();
  const [getVehicleModelsApiHandler, getVehicleModelsApiData] = useGetVehicleModelsMutation();
  const { onCancelAuctionHandler } = useCancelAuctionHandler();
  // custom hooks end...

  // Handlers start...
  const fetchPaginationDataHandler = async ({ params }) => {
    let res;
    setPaginationData((paginationData) => {
      return {
        ...paginationData,
        isFetching: true,
      };
    });

    if (value === 0) {
      res = await getFetchOwnCarsApiHandler({
        params,
      });
    } else if (value === 1) {
      res = await getFetchOtherCarsApiHandler({
        params,
      });
    }

    setPaginationData((paginationData) => {
      return {
        ...paginationData,
        data: res?.data?.Vehicle?.data,
        isFetching: false,
      };
    });
  };

  const handlePageChange = (e, page) => {
    setPaginationData((paginationData) => {
      return {
        ...paginationData,
        current_page: page,
      };
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  // Handlers end...

  // useEffect start...
  React.useEffect(() => {
    if (
      (params.Make && params.Make.length > 0) ||
      (params.Model && params.Model.length > 0) ||
      params.from_year ||
      params.to_year ||
      params.from_Mileage ||
      params.to_Mileage ||
      params.auction_type ||
      params.page ||
      params.per_page
    ) {
      const debouncedFetchData = debounce(async () => {
        await fetchPaginationDataHandler({
          params,
        });
      }, 1000);

      debouncedFetchData();

      return () => {
        debouncedFetchData.cancel(); // Cleanup function to cancel pending calls
      };
    }
  }, [params]);

  React.useEffect(() => {
    (async () => {
      const res = await getLiveAuctionVehicleMakesApiHandler({
        endpoint: `/get-year-make`,
      });

      setAllMakes(
        res?.data?.Make?.map?.((item) => {
          return {
            value: item?.make_id,
            label: item?.make_name,
          };
        }) || []
      );

      setAllYears(res?.data?.Year || []);
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      await fetchPaginationDataHandler({
        params,
      });
    })();
  }, [value, paginationData?.current_page, pageTab]);

  // Fixed: Removed the problematic useEffect that was forcing [""] arrays
  React.useEffect(() => {
    (async () => {
      if (selectedMakes && selectedMakes.length > 0) {
        const res = await getVehicleModelsApiHandler({
          endpoint: `/get-model`,
          payload: {
            makes: selectedMakes
              ?.filter((item) => item && item.value)
              ?.map?.((item) => item?.value)
              ?.join(','),
          },
        });

        const allModels = [];

        if (res?.data?.Model) {
          for (let i = 0; i < Object.entries(res?.data?.Model)?.length; i++) {
            allModels.push(...(Object.entries(res?.data?.Model)?.[i]?.[1] || []));
          }

          setAllModels(
            allModels?.map?.((item) => {
              return {
                value: item?.model_id,
                label: item?.model_name,
              };
            }) || []
          );
        }
      } else {
        setAllModels([]);
        setSelectedModels([]); // Clear models when makes are cleared
      }
    })();
  }, [selectedMakes]);

  React.useEffect(() => {
    if (tabValue === 0) {
      setPageTab(pageTabs.liveAuction);
    } else {
      setPageTab(pageTabs.buyNow);
    }
  }, [tabValue]);

  React.useEffect(() => {
    setMyCarsOrOtherCars(value);
  }, [
    value
  ])
  // useEffect end...

  console.log(selectedMakes, 'UpcomingCarsList');

  return (
    <Box
      display={'flex'}
      width={'100%'}
      sx={{
        borderRadius: '24px',
        height: { md: `calc(100vh - 92px)`, sm: `calc(100vh - 112px)`, xs: 'auto' },
        position: 'relative',
      }}
    >
      <Box
        sx={{
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          background: theme.palette.background.paper,
          borderRadius: '24px',
          width: '100%',
        }}
      >
        <Paper elevation={0} sx={{ borderRadius: '24px' }}>
          <Box sx={{ padding: { xs: 2, sm: 3, md: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 1 },
              }}
            >
              <Typography
                variant={'h1'}
                sx={{
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}
              >
                {t('upcomingCars')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      border: '1px solid',
                      borderColor: 'border.main',
                      p: 0.5,
                      borderRadius: 2,
                      overflow: 'hidden',
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    <Tabs
                      fullWidth
                      value={tabValue}
                      onChange={handleTabChange}
                      slotProps={{
                        indicator: { style: { display: 'none' } },
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiTabs-flexContainer': {
                          height: '100%',
                        },
                        '& .MuiTab-root': {
                          color: 'text.primary',
                          padding: { xs: '0.5rem 1rem', sm: '0.75rem 1.5rem' },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          textTransform: 'none',
                          fontWeight: 500,
                          minHeight: { xs: '36px', sm: '48px' },
                          '&.Mui-selected': {
                            borderRadius: 2,
                            backgroundColor: 'primary.main',
                            color: 'white',
                          },
                        },
                      }}
                    >
                      <Tab fullWidth sx={{ width: '50%', textWrap: 'nowrap' }} label={t('liveAuction')} />
                      <Tab fullWidth sx={{ width: '50%', textWrap: 'nowrap' }} label={t('buyNow')} />
                    </Tabs>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: 'border.main',
                      p: 0.5,
                      borderRadius: 2,
                      overflow: 'hidden',
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    <MyCarsOrOtherCarsButton value={value} setValue={setValue} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Filters Section */}
            <Grid container spacing={{ xs: 2, sm: 2, md: 2 }} sx={{ mt: 2 }}>
              {/* Select Make */}
              <Grid size={{ xs: 12, sm: 6, md: 6, xl: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <InputLabel>{t('selectMake')}</InputLabel>
                <Select
                  value={selectedMakes}
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? [] : e.target.value;
                    setSelectedMakes(value.filter((item) => item && item.value));
                  }}
                  displayEmpty
                  fullWidth
                  multiple={true}
                  renderValue={(selected) => getSelectedMakesRenderValue(selected)}
                  endAdornment={
                    <Box
                      sx={{
                        paddingRight: '1rem',
                      }}
                    >
                      {selectedMakes?.filter((item) => item && item.value)?.length > 0 ? (
                        <Button
                          sx={{
                            padding: '0',
                            minWidth: '0',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMakes([]);
                          }}
                        >
                          <CancelTwoTone
                            sx={{
                              color: theme.palette.text.primary,
                            }}
                          />
                        </Button>
                      ) : null}
                    </Box>
                  }
                >
                  {allMakes?.map?.((make) => {
                    return (
                      <MenuItem key={make?.value} value={make}>
                        <ListItemText primary={make?.label} />
                        {selectedMakes?.some((selected) => selected?.value === make?.value) ? <Check /> : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
            </Grid>

            {/* Select Model */}
              <Grid size={{ xs: 12, sm: 6, md: 6, xl: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <InputLabel>{t('selectModel')}</InputLabel>
                  <Select
                  value={selectedModels}
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? [] : e.target.value;
                    setSelectedModels(value.filter((item) => item && item.value));
                  }}
                  displayEmpty
                  fullWidth
                  multiple={true}
                  disabled={selectedMakes.filter((item) => item && item.value).length === 0}
                  renderValue={(selected) => getSelectedModelsRenderValue(selected)}
                  endAdornment={
                    <Box
                      sx={{
                        paddingRight: '1rem',
                      }}
                    >
                      {selectedModels?.filter((item) => item && item.value)?.length > 0 ? (
                        <Button
                          sx={{
                            padding: '0',
                            minWidth: '0',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedModels([]);
                          }}
                        >
                          <CancelTwoTone
                            sx={{
                              color: theme.palette.text.primary,
                            }}
                          />
                        </Button>
                      ) : null}
                    </Box>
                  }
                >
                  {allModels?.map((item) => {
                    return (
                      <MenuItem key={item?.value} value={item}>
                        <ListItemText primary={item?.label} />
                        {selectedModels?.some((selected) => selected?.value === item?.value) ? <Check /> : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
            </Grid>

            {/* Select Year */}
            <Grid size={{ xs: 12, sm: 6, md: 6, xl: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <InputLabel>{t('selectYear')}</InputLabel>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                  }}
                >
                  <Select
                    value={selectedYearFrom}
                    onChange={(e) => setSelectedYearFrom(e.target.value)}
                    displayEmpty
                    fullWidth
                    endAdornment={
                      <Box
                        sx={{
                          paddingRight: '1rem',
                        }}
                      >
                        {selectedYearFrom ? (
                          <Button
                            sx={{
                              padding: '0',
                              minWidth: '0',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedYearFrom('');
                            }}
                          >
                            <CancelTwoTone
                              sx={{
                                color: theme.palette.text.primary,
                              }}
                            />
                          </Button>
                        ) : null}
                      </Box>
                    }
                  >
                    <MenuItem value="">{t('from')}</MenuItem>
                    {years?.map((year) => {
                      return (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>

                  <Select
                    value={selectedYearTo}
                    onChange={(e) => setSelectedYearTo(e.target.value)}
                    displayEmpty
                    fullWidth
                    endAdornment={
                      <Box
                        sx={{
                          paddingRight: '1rem',
                        }}
                      >
                        {selectedYearTo ? (
                          <Button
                            sx={{
                              padding: '0',
                              minWidth: '0',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedYearTo('');
                            }}
                          >
                            <CancelTwoTone
                              sx={{
                                color: theme.palette.text.primary,
                              }}
                            />
                          </Button>
                        ) : null}
                      </Box>
                    }
                  >
                    <MenuItem value="">{t('to')}</MenuItem>
                    {years?.map((year) => {
                      return (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Box>
              </Box>
            </Grid>

            {/* Odometer */}
            <Grid size={{ xs: 12, sm: 6, md: 6, xl: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <InputLabel>{t('odometer')}</InputLabel>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                  }}
                >
                  <TextField
                    type="number"
                    value={odometerFrom}
                    onChange={(e) => setOdometerFrom(e.target.value)}
                    placeholder={t('fromKm')}
                    fullWidth
                  />
                  <TextField
                    type="number"
                    value={odometerTo}
                    onChange={(e) => setOdometerTo(e.target.value)}
                    placeholder={t('toKm')}
                    fullWidth
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Selected Brands */}
            {selectedMakes?.filter((item) => item && item.value)?.length > 0 ? (
              <Box
                sx={{
                  marginTop: '0.25rem',
                }}
              >
                <HorizontalChipsWithTooltip arr={selectedMakes} setSelectedArr={setSelectedMakes} />
              </Box>
            ) : null}
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          height: 'calc(100% - 96px)',
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          scrollbarWidth: 'none !important' /* Firefox */,
          '-ms-overflow-style': 'none' /* IE and Edge */,
          padding: { xs: 2, sm: 3, md: 3 },
          marginBottom: { xs: 3, sm: 3, md: 3 },
        }}
      >
        {paginationData?.isFetching ? (
          // Skeleton UI
          <Box sx={{ height: '100%', width: '100%' }}>
            <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
              {[...Array(8)].map((_, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: 2,
                      padding: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    {/* Car image skeleton */}
                    <Box sx={{ position: 'relative', marginBottom: 2 }}>
                      <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1.5 }} />

                      {/* Timer skeleton in top right */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                        }}
                      >
                        <Skeleton variant="rectangular" width={130} height={40} sx={{ borderRadius: 1 }} />
                      </Box>
                    </Box>

                    {/* Car details skeleton */}
                    <Box sx={{ marginBottom: 2 }}>
                      {/* Car title */}
                      <Skeleton variant="text" width="80%" height={24} sx={{ marginBottom: 1 }} />

                      {/* VIN */}
                      <Skeleton variant="text" width="60%" height={16} sx={{ marginBottom: 1 }} />

                      {/* Car specs grid */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, marginBottom: 2 }}>
                        {[...Array(4)].map((_, specIndex) => (
                          <Box key={specIndex}>
                            <Skeleton variant="text" width="50%" height={14} sx={{ marginBottom: 0.5 }} />
                            <Skeleton variant="text" width="70%" height={16} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : paginationData?.data?.length === 0 ? (
          <Typography
            sx={{
              fontWeight: '500',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
              textAlign: 'center',
              marginTop: { xs: '-100px', md: '-200px' },
            }}
          >
            {t('No cars are listed at the moment. Please check back later as new vehicles arrive regularly.')}
          </Typography>
        ) : (
          <Box sx={{ height: '100%', width: '100%', padding: { xs: 0, sm: 0 } }}>
              <CarsList
                isUpcomingCar={true}
                cars={paginationData?.data?.map?.((vehicle) => {
                  return {
                    ...vehicle,
                    isInUpcomingCars: true,
                    // dontShowCountDown: true,
                    dontShowOrSeparator: true,
                    // dontShowBuyNow: true,
                    dontShowMakeOffer: true,
                    liveAuctionOrBuyNowTab: tabValue === 0 ? auctionTypes.liveAuction : auctionTypes.buyNow,
                    // dontShowPlaceABid: true,
                    vin: vehicle?.VIN,
                    year: vehicle?.Year,
                    make: vehicle?.Make,
                    model: vehicle?.Model,
                    odometer: vehicle?.Mileage,
                    image: vehicle?.Exterior_Image
                      ? `${baseImageUrl}/${vehicle?.Vehicle_Id}/${vehicle?.Exterior_Image}`
                      : ``,
                  };
                })}
                onClick={(vehicle) => {
                  console.log(vehicle, 'CarsList');
                  if (onCarClick) {
                    onCarClick(vehicle, pageTab);
                  }
                }}
                Component={CarContainer1}
                ChildComponent={tabValue === 0 ? CarListingCard : VehicleListingCard}
                showCancelAuction={true}
                onCancelAuction={onCancelAuctionHandler}
                showMenuButton={false}
                tab={auctionTypes[pageTabsReverseMapping[pageTab]]}
                cardType={carContainerTypes.UPCOMING_CARS}
                timerRemainingSecondsHandler={getUpcomingCarRemainingSeconds}
              />
            </Box>
          )}
        </Box>
      </Box>
      {/* Pagination */}
      {paginationData?.last_page > 1 ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            bottom: '0px',
            padding: { xs: 1, sm: 0 },
          }}
        >
          <Pagination
            count={paginationData?.last_page}
            page={paginationData?.current_page}
            onChange={handlePageChange}
            size={isMobile ? 'small' : 'medium'}
            siblingCount={isMobile ? 0 : isTablet ? 1 : 1}
            boundaryCount={isMobile ? 1 : 2}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default UpcomingCarsList;
